import { UkDateInput } from "@/components/UkDateInput";

import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { PoundSterling, Download, Calendar, Plus, Send, Printer, X, Trash2 } from "lucide-react";
import { HeaderAccent } from "@/lib/status";
import { PayRatesPanel } from "@/components/PayRatesPanel";
import { PayrollRuns } from "@/components/PayrollRuns";

const iso = (d) => d.toISOString().slice(0, 10);
function monthRange() {
  const now = new Date();
  const from = new Date(now.getFullYear(), now.getMonth(), 1);
  const to = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  return { from: iso(from), to: iso(to) };
}

export default function Payroll() {
  const init = monthRange();
  const [from, setFrom] = useState(init.from);
  const [to, setTo] = useState(init.to);
  const [data, setData] = useState(null);

  const load = () => api.get(`/payroll?date_from=${from}&date_to=${to}`).then((r) => setData(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [from, to]);

  const [adjustFor, setAdjustFor] = useState(null);
  const [adjForm, setAdjForm] = useState({ label: "", amount: "" });
  const [busy, setBusy] = useState(false);
  const saveAdj = async () => {
    if (!adjForm.label.trim() || adjForm.amount === "") { toast.error("Add a label and amount (use a minus for a deduction)"); return; }
    setBusy(true);
    try { await api.post("/payroll/adjustment", { staff_id: adjustFor.carer_id, period_from: from, period_to: to, label: adjForm.label.trim(), amount: Number(adjForm.amount) }); toast.success("Adjustment added"); setAdjustFor(null); setAdjForm({ label: "", amount: "" }); load(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const delAdj = async (id) => { try { await api.delete(`/payroll/adjustment/${id}`); load(); } catch (e) { toast.error(apiError(e)); } };
  const lineText = (r) => (r.pay_lines || []).map((l) => `${l.label}: ${l.kind === "phone" ? `${l.qty} wk` : `${l.qty}h`} £${l.amount.toFixed(2)}`).join(" · ");
  const sendPayslip = async (r) => {
    const summary = `Visits: ${r.visits} · Hours: ${r.hours} · ${lineText(r)} · Base £${r.base_pay.toFixed(2)} · Mileage (paid separately) £${r.mileage_pay.toFixed(2)}${r.adjust_total ? ` · Adjustments £${r.adjust_total.toFixed(2)}` : ""}`;
    try { await api.post("/payroll/send", { staff_id: r.carer_id, period_from: from, period_to: to, gross_pay: r.gross_pay, summary }); toast.success(`Payslip sent to ${r.carer_name}`); }
    catch (e) { toast.error(apiError(e)); }
  };
  const printPayslip = (r) => {
    const lines = (r.pay_lines || []).map((l) => `<tr><td>${l.label} (${l.kind === "phone" ? `${l.qty} client-week(s)` : `${l.qty}h`})</td><td style="text-align:right">£${l.amount.toFixed(2)}</td></tr>`).join("");
    const adj = (r.adjustments || []).map((a) => `<tr><td>${a.label}</td><td style="text-align:right">£${Number(a.amount).toFixed(2)}</td></tr>`).join("");
    const w = window.open("", "_blank");
    if (!w) { toast.error("Please allow pop-ups to download the payslip"); return; }
    w.document.write(`<html><head><title>Payslip ${r.carer_name}</title><style>body{font-family:Arial,sans-serif;padding:40px;color:#1a2b28}h1{color:#0F5A54;margin:0}table{width:100%;border-collapse:collapse;margin-top:16px}td,th{border-bottom:1px solid #ddd;padding:8px;font-size:13px;text-align:left}.tot{margin-top:16px;text-align:right;font-size:18px;font-weight:bold;color:#0F5A54}</style></head><body><h1>Brightside Befriending Services</h1><h3>Payslip — ${r.carer_name}</h3><p><b>Period:</b> ${from} → ${to}</p><table><tr><td>Visits</td><td style="text-align:right">${r.visits}</td></tr><tr><td>Hours</td><td style="text-align:right">${r.hours}</td></tr>${lines}<tr><td><b>Base pay</b></td><td style="text-align:right"><b>£${r.base_pay.toFixed(2)}</b></td></tr><tr><td>Mileage — paid separately (${r.mileage} mi × £${r.mileage_rate})</td><td style="text-align:right">£${r.mileage_pay.toFixed(2)}</td></tr>${adj}</table><p class="tot">Gross pay: £${r.gross_pay.toFixed(2)}</p><script>window.onload=function(){window.print();}</script></body></html>`);
    w.document.close();
  };

  const exportCsv = () => {
    if (!data) return;
    const header = "Befriender,Visits,Hours,Rate,Breakdown,Base Pay,Mileage,Mileage Pay,Gross Pay\n";
    const rows = data.rows.map((r) =>
      `${r.carer_name},${r.visits},${r.hours},${r.hourly_rate},"${lineText(r)}",${r.base_pay},${r.mileage},${r.mileage_pay},${r.gross_pay}`).join("\n");
    const blob = new Blob([header + rows], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `payroll_${from}_${to}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const input = "rounded-lg border border-border bg-white px-3 py-2 text-sm outline-none focus:border-brand focus:ring-2 focus:ring-brand/20";

  return (
    <div className="space-y-6" data-testid="payroll-page">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-ink">Payroll</h1>
          <p className="mt-1 text-ink-soft">Based on completed visits in the selected period. Companion visits at each befriender's rate (£14/hr), respite, care home &amp; hospital at £16/hr, telephone companion £14 a week per client. Mileage at 45p a mile is paid separately.</p>
          <HeaderAccent />
        </div>
        <button onClick={exportCsv} data-testid="export-payroll-button"
          className="inline-flex items-center gap-2 rounded-lg bg-brand px-4 py-2.5 font-semibold text-white transition-all hover:-translate-y-0.5 hover:bg-brand-dark">
          <Download size={18} /> Export CSV
        </button>
      </div>

      <div className="flex flex-wrap items-end gap-4 rounded-xl border border-border bg-white p-4 shadow-sm">
        <div>
          <label className="mb-1 flex items-center gap-1 text-sm font-medium text-ink"><Calendar size={14} /> From</label>
          <UkDateInput testid="payroll-from-input" value={from} onChange={setFrom} />
        </div>
        <div>
          <label className="mb-1 flex items-center gap-1 text-sm font-medium text-ink"><Calendar size={14} /> To</label>
          <UkDateInput testid="payroll-to-input" value={to} min={from} onChange={setTo} />
        </div>
        {data && (
          <div className="ml-auto rounded-lg bg-sage-light px-5 py-3">
            <p className="text-xs font-semibold uppercase tracking-wider text-sage-dark">Total gross pay</p>
            <p className="font-heading text-2xl font-bold text-sage-dark">£{data.total.toFixed(2)}</p>
          </div>
        )}
      </div>

      <PayrollRuns from={from} to={to} />

      <div className="overflow-hidden rounded-xl border border-border bg-white shadow-sm">
        <div className="overflow-x-auto scrollbar-thin">
          <table className="w-full text-sm">
            <thead className="bg-ivory text-left text-xs font-semibold uppercase tracking-wider text-ink-muted">
              <tr>
                <th className="px-6 py-3">Befriender</th>
                <th className="px-6 py-3">Visits</th>
                <th className="px-6 py-3">Hours</th>
                <th className="px-6 py-3">Rate</th>
                <th className="px-6 py-3">Base pay</th>
                <th className="px-6 py-3">Mileage</th>
                <th className="px-6 py-3">Adjustments</th>
                <th className="px-6 py-3">Gross pay</th>
                <th className="px-6 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {data?.rows.map((r) => (
                <tr key={r.carer_id} data-testid={`payroll-row-${r.carer_id}`} className="hover:bg-ivory/60">
                  <td className="px-6 py-4 font-semibold text-ink">{r.carer_name}</td>
                  <td className="px-6 py-4 text-ink-soft">{r.visits}</td>
                  <td className="px-6 py-4 text-ink-soft">{r.hours}</td>
                  <td className="px-6 py-4 text-ink-soft" data-testid={`payroll-rate-${r.carer_id}`}>{(r.rates_used || []).length > 1 ? r.rates_used.map((x) => `£${x}`).join(" → ") : `£${r.hourly_rate}`}</td>
                  <td className="px-6 py-4 text-ink-soft" data-testid={`payroll-base-${r.carer_id}`}>
                    <p>£{r.base_pay.toFixed(2)}</p>
                    {(r.pay_lines || []).map((l) => (
                      <p key={l.kind} className="text-[11px] text-ink-muted" data-testid={`payline-${l.kind}-${r.carer_id}`}>{l.label}: {l.kind === "phone" ? `${l.qty} wk` : `${l.qty}h`} · £{l.amount.toFixed(2)}</p>
                    ))}
                  </td>
                  <td className="px-6 py-4 text-ink-soft">{r.mileage} mi · £{r.mileage_pay.toFixed(2)}</td>
                  <td className="px-6 py-4 text-ink-soft">
                    {(r.adjustments || []).length === 0 ? <span className="text-ink-muted">—</span> : (
                      <div className="space-y-1">
                        {r.adjustments.map((a) => (
                          <div key={a.id} className="flex items-center gap-1.5 text-xs">
                            <span className={a.amount < 0 ? "text-coral-dark" : "text-sage-dark"}>{a.label}: £{a.amount.toFixed(2)}</span>
                            <button onClick={() => delAdj(a.id)} data-testid={`del-adj-${a.id}`} className="text-ink-muted hover:text-coral"><Trash2 size={11} /></button>
                          </div>
                        ))}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 font-heading font-bold text-ink">£{r.gross_pay.toFixed(2)}</td>
                  <td className="px-6 py-4">
                    <div className="flex justify-end gap-2">
                      <button onClick={() => setAdjustFor(r)} data-testid={`adjust-${r.carer_id}`} title="Add adjustment" className="rounded-lg border border-border px-2.5 py-1.5 text-ink hover:bg-secondary"><Plus size={14} /></button>
                      <button onClick={() => printPayslip(r)} data-testid={`print-payslip-${r.carer_id}`} title="Download payslip" className="rounded-lg border border-border px-2.5 py-1.5 text-ink hover:bg-secondary"><Printer size={14} /></button>
                      <button onClick={() => sendPayslip(r)} data-testid={`send-payslip-${r.carer_id}`} title="Send to inbox" className="rounded-lg border border-brand bg-brand/5 px-2.5 py-1.5 text-brand hover:bg-brand/10"><Send size={14} /></button>
                    </div>
                  </td>
                </tr>
              ))}
              {data?.rows.length === 0 && (
                <tr><td colSpan={9} className="px-6 py-12 text-center text-ink-muted">
                  <PoundSterling size={28} className="mx-auto mb-2 opacity-40" />
                  No completed visits in this period.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <PayRatesPanel onChanged={load} />

      {adjustFor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => setAdjustFor(null)} />
          <div data-testid="adjustment-modal" className="relative z-10 w-full max-w-sm rounded-xl bg-white p-6 shadow-xl">
            <div className="mb-1 flex items-center justify-between">
              <h2 className="font-heading text-xl font-bold text-ink">Pay adjustment</h2>
              <button onClick={() => setAdjustFor(null)}><X size={20} className="text-ink-muted" /></button>
            </div>
            <p className="mb-4 text-sm text-ink-soft">For <span className="font-semibold">{adjustFor.carer_name}</span> · {from} → {to}. Use a negative amount for a deduction.</p>
            <input className={`${input} w-full`} data-testid="adj-label-input" placeholder="e.g. Extra time, Bonus, Deduction" value={adjForm.label} onChange={(e) => setAdjForm({ ...adjForm, label: e.target.value })} />
            <input type="number" step="0.01" className={`${input} mt-3 w-full`} data-testid="adj-amount-input" placeholder="Amount (£)" value={adjForm.amount} onChange={(e) => setAdjForm({ ...adjForm, amount: e.target.value })} />
            <div className="mt-5 flex gap-3">
              <button onClick={() => setAdjustFor(null)} className="flex-1 rounded-lg border border-border py-2.5 font-medium text-ink hover:bg-secondary">Cancel</button>
              <button onClick={saveAdj} disabled={busy} data-testid="save-adjustment-button" className="flex-1 rounded-lg bg-brand py-2.5 font-semibold text-white hover:bg-brand-dark disabled:opacity-60">Add</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
